import { ISelectOption, Select, SelectSummary } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import styled from 'styled-components';
import { useAppSelector } from '../../appStore';
import ContentConstants from '../../contentConstants';
import { IDisplayLogicCondition, IDisplayLogicQuestion } from '../../models';
import { displayLogicQuestionToSelectOption } from '../../store/QuestionBranchingLogicSlice';
import { selectSurveyQuestion } from '../../store/SurveyStore';
import { assertNonNullable } from '../../utilities/assertions';
import { ConditionText, SelectConditionContainer } from '../common';

export interface IPreviousQuestionBranchingLogicProps {
	surveyQuestionId: number;
	condition: IDisplayLogicCondition;
	unusedDisplayLogicQuestions: IDisplayLogicQuestion[];
	HandleQuestionChange: (surveyQuestionId: number, questionText: string, index: number) => void;
	HandleResponseChange: (responses: string[], index: number) => void;
	HandleSelectionDismissal: (conditionIndex: number, values: ISelectOption[]) => void;
	conditionMessages: string[];
	valueMessages: string[];
}

export const ResponsesContainer = styled.div`
	display: inline-grid;
`;
ResponsesContainer.displayName = 'ResponsesContainer';

export const SelectSummaryContainer = styled.div`
	margin-top: 7px;
`;

export const PreviousQuestionBranchingLogic = ({
	surveyQuestionId,
	condition,
	HandleQuestionChange,
	HandleResponseChange,
	HandleSelectionDismissal,
	conditionMessages,
	unusedDisplayLogicQuestions,
	valueMessages,
}: IPreviousQuestionBranchingLogicProps) => {
	const question = useAppSelector((state) => selectSurveyQuestion(state, surveyQuestionId));
	assertNonNullable(question);
	assertNonNullable(question.displayOrder);

	return (
		<div className='row'>
			<ConditionText className='tws-condition-text col-2'>If answer to </ConditionText>
			<SelectConditionContainer className='tws-question-selector col-5'>
				<Select
					selectWidth='100%'
					displayText={ContentConstants.DISPLAY_LOGIC_PREVIOUS_QUESTION_DROPDOWN_TEXT}
					onApply={onApplyQuestion}
					selectedValue={getSelectedQuestion()}
					items={getQuestionItems()}
					disabled={unusedDisplayLogicQuestions.length === 0}
					message={conditionMessages}
					error={conditionMessages.length !== 0}
				/>
			</SelectConditionContainer>
			<ConditionText className='col-1 text-center'> is </ConditionText>
			<ResponsesContainer className='tws-response-options-selector col-3'>
				<Select
					displayText={ContentConstants.DISPLAY_LOGIC_PREVIOUS_QUESTION_RESPONSES_DROPDOWN_TEXT}
					onApply={onApplyResponses}
					multiple={true}
					disabled={getSelectedQuestion() === ''}
					selectedValues={getSelectedResponses()}
					items={getQuestionResponses()}
					message={valueMessages}
					error={valueMessages.length !== 0}
					showCount={true}
				/>
				<SelectSummaryContainer>
					<SelectSummary enableDismiss={true} selected={getSelectedResponsesSummary()} updateSelect={handleSelectionDismissal} />
				</SelectSummaryContainer>
			</ResponsesContainer>
		</div>
	);

	function getSelectedResponsesSummary(): ISelectOption[] {
		return getSelectedResponses().map((r) => ({ displayText: r, name: r }));
	}

	function getSelectedResponses(): string[] {
		return condition.displayLogicValues.map((dlv) => {
			assertNonNullable(dlv.valueText);
			return dlv.valueText;
		});
	}

	function getQuestionResponses() {
		const selectedQuestionId = getSelectedQuestion();
		if (selectedQuestionId !== '') {
			const selectedQuestion = unusedDisplayLogicQuestions.find((q) => q.surveyQuestionId === +selectedQuestionId);
			if (selectedQuestion) {
				const questionResponses = selectedQuestion.responseOptionGroups.map((r) => ({ displayText: r, name: r }));
				return [
					{ displayText: ContentConstants.NO_RESPONSE, name: '' + ContentConstants.NO_RESPONSE, disabled: selectedQuestion.isRequired },
					...questionResponses,
				];
			}
		}
		return [];
	}

	function getSelectedQuestion(): string {
		return condition.conditionSourceId ?? '';
	}

	function onApplyQuestion(values: string | string[] | undefined) {
		const selectedQuestionId = parseInt(values as string, 0);
		const selectedQuestion = unusedDisplayLogicQuestions.find((q) => q.surveyQuestionId === selectedQuestionId);
		assertNonNullable(selectedQuestion);

		HandleQuestionChange(selectedQuestionId, selectedQuestion.text, condition.index);
	}

	function onApplyResponses(values: string | string[] | undefined) {
		HandleResponseChange(values as string[], condition.index);
	}

	function getQuestionItems(): ISelectOption[] {
		return unusedDisplayLogicQuestions.map(displayLogicQuestionToSelectOption);
	}

	function handleSelectionDismissal(values: ISelectOption[]) {
		HandleSelectionDismissal(condition.index, values);
	}
};
